/**
 * The desktop-app frame for every /app screen: a full-width top bar
 * (wordmark, quick-nav search, user avatar) above a sticky white sidebar
 * card on the cream canvas (greeting, area-model nav, pinned Settings +
 * sign-out), with the existing AppNav rendered as the mobile fallback above
 * the content. Replaces the per-page `<div><AppNav /></div>` wrapper — see
 * docs/plans/2026-08-24-ui-redesign-sunlit-cockpit.md "Revision 2".
 */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import {
  BarChart3,
  ChevronDown,
  ClipboardList,
  Columns2,
  CreditCard,
  FileText,
  FlaskConical,
  LayoutDashboard,
  LogOut,
  PackageOpen,
  Receipt,
  RefreshCw,
  Search,
  Settings as SettingsIcon,
  Store,
  Tags,
  Truck,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { AppNav } from './AppNav';
import { useMe, useSignout } from '../lib/auth';
import { isNavItemVisible } from '../lib/nav';
import { loadCollapsedSections, saveCollapsedSections } from '../lib/sidebarSections';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Module gate; undefined = always shown for tenant-side roles. */
  module?: string;
  warehouseVisible?: boolean;
  roles?: readonly string[];
  /** Grantable 'va' permission key — owner/operator always pass; a 'va'
   *  session needs it in me.user.permissions. See lib/nav.ts. */
  permission?: 'planning';
  exact?: boolean;
}

interface NavGroup {
  eyebrow: string;
  items: NavItem[];
}

const TODAY_ITEM: NavItem = { to: '/app', label: 'Today', icon: LayoutDashboard, exact: true };
const SETTINGS_ITEM: NavItem = { to: '/app/settings', label: 'Settings', icon: SettingsIcon };

export const NAV_GROUPS: NavGroup[] = [
  {
    eyebrow: 'PROCUREMENT',
    items: [
      { to: '/app/brands', label: 'Brands', icon: Store },
      { to: '/app/pipeline', label: 'Pipeline', icon: Columns2 },
      { to: '/app/orders', label: 'Orders', icon: FileText, module: 'orders' },
    ],
  },
  {
    eyebrow: 'WAREHOUSE',
    items: [
      {
        to: '/app/receiving',
        label: 'To receive',
        icon: PackageOpen,
        module: 'warehouse',
        warehouseVisible: true,
        exact: true,
      },
      {
        to: '/app/warehouse/prep',
        label: 'Prep workstation',
        icon: PackageOpen,
        module: 'warehouse',
        warehouseVisible: true,
        exact: true,
      },
      {
        to: '/app/warehouse/packing',
        label: 'Amazon shipment packing',
        icon: Truck,
        module: 'warehouse',
        warehouseVisible: true,
        exact: true,
      },
      {
        to: '/app/warehouse/charges',
        label: 'Work charges',
        icon: FileText,
        module: 'warehouse',
        warehouseVisible: true,
        exact: true,
      },
      {
        to: '/app/warehouse/invoices',
        label: 'Invoices',
        icon: Receipt,
        module: 'warehouse',
        warehouseVisible: true,
        exact: true,
      },
    ],
  },
  {
    eyebrow: 'OPS',
    items: [
      { to: '/app/shipments', label: 'Shipments', icon: Truck, module: 'shipments' },
      { to: '/app/amazon', label: 'Amazon', icon: RefreshCw, module: 'amazon' },
      { to: '/app/sales', label: 'Sales', icon: Receipt, module: 'amazon' },
      {
        to: '/app/price-calibration',
        label: 'Price Calibration',
        icon: Tags,
        module: 'amazon',
      },
      { to: '/app/planning', label: 'Planning', icon: ClipboardList, permission: 'planning' },
      { to: '/app/research', label: 'Research', icon: FlaskConical, permission: 'planning' },
    ],
  },
  {
    eyebrow: 'FINANCE',
    items: [
      { to: '/app/finance', label: 'Finance', icon: Wallet, exact: true },
      {
        to: '/app/finance/fee-center',
        label: 'Fee Center',
        icon: Receipt,
        module: 'amazon',
        roles: ['owner', 'operator'],
      },
      {
        to: '/app/expenses',
        label: 'Expenses',
        icon: CreditCard,
        roles: ['owner'],
      },
    ],
  },
  {
    eyebrow: 'REPORTS',
    items: [{ to: '/app/reports', label: 'Reports', icon: BarChart3 }],
  },
];

const itemBase =
  'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-fg-secondary hover:text-fg-primary hover:bg-surface-elev transition-colors duration-fast';
const itemActive = 'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm bg-anchor text-canvas';
const sectionHeaderBase =
  'w-full flex items-center justify-between gap-2 px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-fg-tertiary hover:text-fg-secondary transition-colors duration-fast';

/** Stable id for a group's `<div>` of links, referenced by its toggle button's aria-controls. */
function sectionContentId(eyebrow: string): string {
  return `sidebar-section-${eyebrow.toLowerCase().replace(/\s+/g, '-')}`;
}

function SidebarLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className={active ? itemActive : itemBase}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {item.label}
    </Link>
  );
}

/**
 * "Welcome, {firstName}" greeting. `User.name` (packages/schemas) is
 * nullable and free-text — prefer its first word when present, otherwise
 * derive from the email local-part (capitalized) since that's always set.
 */
function firstNameFrom(user: { name: string | null; email: string } | undefined): string {
  if (!user) return '';
  const fromName = user.name?.trim().split(/\s+/)[0];
  if (fromName) return fromName;
  const local = user.email.split('@')[0] ?? '';
  return local.charAt(0).toUpperCase() + local.slice(1);
}

/**
 * Quick-nav search: filters the same destinations the sidebar shows.
 * Cmd/Ctrl+K focuses it; Arrow keys move selection; Enter navigates;
 * Escape closes. Client-side only, no backend involved.
 */
function QuickNavSearch({ items }: { items: NavItem[] }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const trimmed = query.trim().toLowerCase();
  const matches = trimmed ? items.filter((item) => item.label.toLowerCase().includes(trimmed)) : [];

  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  function select(item: NavItem) {
    navigate({ to: item.to });
    setQuery('');
    setOpen(false);
  }

  return (
    <div className="max-w-md w-full mx-auto relative">
      <Search
        className="h-4 w-4 text-fg-tertiary absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        type="text"
        value={query}
        placeholder="Search..."
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
          } else if (event.key === 'Enter') {
            const match = matches[activeIndex];
            if (match) {
              event.preventDefault();
              select(match);
            }
          } else if (event.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        className="w-full bg-surface border border-border-subtle rounded-full pl-9 pr-14 py-2 text-sm placeholder:text-fg-tertiary"
      />
      <span className="text-[10px] font-mono text-fg-tertiary border border-border-subtle rounded-md px-1.5 py-0.5 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        ⌘K
      </span>
      {open && trimmed && (
        <div className="absolute top-full mt-2 w-full bg-surface border border-border-subtle rounded-xl shadow-sm overflow-hidden z-20">
          {matches.length === 0 ? (
            <div className="px-3 py-2 text-sm text-fg-tertiary">No matches</div>
          ) : (
            matches.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.to}
                  type="button"
                  // Fires before the input's blur, so the click still lands.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(item)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors duration-fast ${
                    index === activeIndex
                      ? 'bg-surface-elev text-fg-primary'
                      : 'text-fg-secondary hover:bg-surface-elev hover:text-fg-primary'
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { data } = useMe();
  const matchRoute = useMatchRoute();
  const signout = useSignout();
  const isWarehouse = data?.user.role === 'warehouse';
  const modules = data?.modules;
  const email = data?.user.email ?? '';
  const firstName = firstNameFrom(data?.user);
  const tenantName = data?.tenant.name;
  const navCtx = {
    role: data?.user.role,
    modules,
    permissions: data?.user.permissions ?? [],
  };

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => isNavItemVisible(item, navCtx)),
  })).filter((group) => group.items.length > 0);

  function isGroupActive(group: NavGroup): boolean {
    return group.items.some((item) => !!matchRoute({ to: item.to, fuzzy: !(item.exact ?? false) }));
  }
  const activeEyebrow = visibleGroups.find(isGroupActive)?.eyebrow;

  // Sections start from whatever's persisted (all-expanded by default — see
  // sidebarSections.ts), except the section holding the current route: a
  // fresh page load must never hide the active link, so it's forced open
  // here regardless of what was last saved. This is a one-time override,
  // not itself persisted — the user can still collapse that section by
  // hand afterward (see toggleSection).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const stored = loadCollapsedSections();
    if (activeEyebrow) stored.delete(activeEyebrow);
    return stored;
  });

  // Re-applies the same "active section can't be hidden" rule on
  // client-side navigation (not just initial mount) — if the section the
  // user just navigated into was collapsed, open it.
  useEffect(() => {
    if (!activeEyebrow) return;
    setCollapsed((prev) => {
      if (!prev.has(activeEyebrow)) return prev;
      const next = new Set(prev);
      next.delete(activeEyebrow);
      return next;
    });
  }, [activeEyebrow]);

  function toggleSection(eyebrow: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(eyebrow)) next.delete(eyebrow);
      else next.add(eyebrow);
      saveCollapsedSections(next);
      return next;
    });
  }

  const searchItems: NavItem[] = [
    ...(!isWarehouse ? [TODAY_ITEM] : []),
    ...visibleGroups.flatMap((group) => group.items),
    ...(!isWarehouse ? [SETTINGS_ITEM] : []),
  ];

  return (
    <div className="min-h-screen bg-canvas text-fg-primary flex flex-col">
      <div className="hidden md:flex items-center gap-4 px-6 h-14 sticky top-0 z-30 bg-canvas border-b border-border-subtle">
        <Link
          to={isWarehouse ? '/app/receiving' : '/app'}
          className="flex items-center gap-2 text-base font-semibold tracking-tight text-fg-primary shrink-0"
        >
          <span className="h-6 w-6 rounded-lg bg-anchor inline-flex items-center justify-center text-canvas text-xs font-bold">
            S
          </span>
          Sourcito
        </Link>

        <div className="flex-1">
          <QuickNavSearch items={searchItems} />
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <span className="h-8 w-8 rounded-full bg-surface-elev text-xs font-medium flex items-center justify-center">
            {email ? email[0]!.toUpperCase() : '?'}
          </span>
          <button
            type="button"
            disabled
            title="Team invites — coming soon"
            className="text-sm px-3.5 py-1.5 rounded-full bg-anchor/90 text-canvas opacity-50 cursor-not-allowed"
          >
            + Invite
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="hidden md:flex w-60 shrink-0">
          <div className="m-3 mr-0 rounded-xl bg-surface border border-border-subtle p-4 flex flex-col sticky top-14 self-start h-[calc(100vh-56px-24px)]">
            <p className="font-display text-lg px-3 pt-1 pb-2">Welcome, {firstName}</p>

            <nav className="mt-1 flex-1 overflow-y-auto">
              {!isWarehouse && (
                <SidebarLink
                  item={TODAY_ITEM}
                  active={!!matchRoute({ to: TODAY_ITEM.to, fuzzy: false })}
                />
              )}

              {visibleGroups.map((group) => {
                const isCollapsed = collapsed.has(group.eyebrow);
                const contentId = sectionContentId(group.eyebrow);
                return (
                  <div key={group.eyebrow}>
                    <button
                      type="button"
                      onClick={() => toggleSection(group.eyebrow)}
                      aria-expanded={!isCollapsed}
                      aria-controls={contentId}
                      className={sectionHeaderBase}
                    >
                      <span>{group.eyebrow}</span>
                      <ChevronDown
                        className={`h-3 w-3 shrink-0 transition-transform duration-fast ${
                          isCollapsed ? '-rotate-90' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                    <div id={contentId}>
                      {!isCollapsed &&
                        group.items.map((item) => (
                          <SidebarLink
                            key={item.to}
                            item={item}
                            active={!!matchRoute({ to: item.to, fuzzy: !(item.exact ?? false) })}
                          />
                        ))}
                    </div>
                  </div>
                );
              })}
            </nav>

            <div className="mt-auto pt-3 border-t border-border-subtle">
              {!isWarehouse && (
                <Link
                  to="/app/settings"
                  className={
                    matchRoute({ to: '/app/settings', fuzzy: true }) ? itemActive : itemBase
                  }
                  aria-current={
                    matchRoute({ to: '/app/settings', fuzzy: true }) ? 'page' : undefined
                  }
                >
                  <SettingsIcon className="h-4 w-4" aria-hidden="true" />
                  Settings
                </Link>
              )}
              <div className="flex items-center justify-between px-3 py-2">
                <span className="h-7 w-7 rounded-full bg-surface-elev text-xs font-medium flex items-center justify-center shrink-0">
                  {email ? email[0]!.toUpperCase() : '?'}
                </span>
                <button
                  type="button"
                  onClick={() => signout.mutate()}
                  aria-label="Sign out"
                  className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast shrink-0"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <div className="px-3 pt-3 mt-1 border-t border-border-subtle">
                {tenantName && (
                  <p className="text-[11px] text-fg-tertiary truncate">{tenantName}</p>
                )}
                <p className="font-mono text-[10px] tabular-nums text-fg-tertiary mt-0.5">
                  v{__APP_VERSION__} · {__APP_COMMIT__}
                </p>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="md:hidden">
            <AppNav />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
